import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { GlobalFocusToggle } from '@els/els-react--utils';
import { Loader } from '@els/els-react--loader';
import AppStorage from '@Services/AppStorage';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import * as WindowHelper from '@Services/WindowHelper';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import withReducer from '@Hooks/withReducer';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { withCombinedRouteParams } from '@AppRoutes/ConnectedRoute';
import SidebarPanel from '../SearchSection/SidebarPanel';
import SearchSection from '../SearchSection';
import HeaderContainer from '../HeaderContainer';
import Controls from '../Controls/Controls';
import QueryPanel from '../QueryPanel';
import queryletReducer from '../QueryletReducer';
import {
  addToBuilderAction,
  initNodeList,
  onToggleQueryletAction,
  getFormsAction,
  getHistoryItemsAction,
  cleanupOnUnmountAction,
} from '../QueryletActions';
import {
  nodesList as nodesListSelector,
  favList as favlistSelector,
  hiddenList as hiddenListSelector,
  properties as propertiesSelector,
  forms as formsSelector,
  historyItems as historyItemsSelector,
  indexedQuerylets as indexedQueryletsSelector,
  indexedForms as indexedFormsSelector,
  importedQuery as importedQuerySelector,
} from '../QuerySelector';

import './QueryContainer.scss';

/* eslint-disable no-shadow */
function QueryContainer({
  nodesList,
  properties,
  favList,
  hiddenList,
  forms,
  historyItems,
  indexedQuerylets,
  indexedForms,
  importedQuery,
  initNodeList,
  addToBuilderAction,
  onToggleQueryletAction,
  getFormsAction,
  getHistoryItemsAction,
  cleanupOnUnmountAction,
  importFormId,
  autoSearch,
}) {
  const qbPanelsStates = AppStorage.instance.get('qbPanelsStates') || {};
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(
    qbPanelsStates.sidebar ? qbPanelsStates.sidebar : WindowHelper.isBreakpointView()
  );
  const location = useLocation();
  const importedQueryStateParamValue = location.state && location.state.importedQuery;

  useEffect(() => {
    initNodeList();
    RouteChangeTracking.trackOnRouteChangeSuccess();
    const skipLinkFooter = document.getElementById('els-skiplink-footer');
    const footerListItem = skipLinkFooter ? skipLinkFooter.parentElement : null;
    if (footerListItem) {
      footerListItem.style.display = 'none'; // Hide the element that contains the skip link for footer
    }
    return () => {
      cleanupOnUnmountAction();
      if (footerListItem) {
        footerListItem.style.display = ''; // Reset display to show the footer element again
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getFormsAction(importFormId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importFormId]);

  const addToBuilderFn = (node) => {
    addToBuilderAction(node);
  };

  return (
    <GlobalFocusToggle>
      {autoSearch && <Loader isVisible />}
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <SidebarPanel isSidebarCollapsed={isSidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
          <SearchSection
            properties={properties}
            forms={forms}
            getForms={getFormsAction}
            historyItems={historyItems}
            indexedQuerylets={indexedQuerylets}
            indexedForms={indexedForms}
            getHistoryItems={getHistoryItemsAction}
            addToBuilder={addToBuilderFn}
          />
        </SidebarPanel>
        <section className="query-builder" aria-label="Query Builder Workspace">
          <header className="query-builder__header">
            <Controls
              nodesList={nodesList}
              favList={favList}
              hiddenList={hiddenList}
              properties={properties}
              importedQuery={importedQuery}
              importedQueryStateParamValue={importedQueryStateParamValue}
              addToBuilder={addToBuilderFn}
            />
          </header>
          <QueryPanel
            className="query-builder__content"
            aria-label="Query Builder Stage"
            nodesList={nodesList}
            onToggleQuerylet={onToggleQueryletAction}
          />
          {/* moved this to ensure it reads after all the querylet in the screen reader */}
          <HeaderContainer nodesList={nodesList} properties={properties} />
        </section>
      </DndProvider>
    </GlobalFocusToggle>
  );
}

QueryContainer.propTypes = {
  nodesList: PropTypes.array,
  properties: PropTypes.array,
  favList: PropTypes.array,
  indexedQuerylets: PropTypes.object,
  indexedForms: PropTypes.object,
  hiddenList: PropTypes.array,
  importedQuery: PropTypes.any,
  forms: PropTypes.array.isRequired,
  historyItems: PropTypes.array.isRequired,
  initNodeList: PropTypes.func,
  addToBuilderAction: PropTypes.func,
  getFormsAction: PropTypes.func.isRequired,
  getHistoryItemsAction: PropTypes.func.isRequired,
  cleanupOnUnmountAction: PropTypes.func.isRequired,
  onToggleQueryletAction: PropTypes.func,
  importFormId: PropTypes.any,
  autoSearch: PropTypes.string,
};

const mapStateToProps = (state) => ({
  nodesList: nodesListSelector(state),
  favList: favlistSelector(state),
  hiddenList: hiddenListSelector(state),
  properties: propertiesSelector(state),
  forms: formsSelector(state),
  historyItems: historyItemsSelector(state),
  indexedQuerylets: indexedQueryletsSelector(state),
  indexedForms: indexedFormsSelector(state),
  importedQuery: importedQuerySelector(state),
});

const mapDispatchToProps = {
  initNodeList,
  addToBuilderAction,
  onToggleQueryletAction,
  getFormsAction,
  getHistoryItemsAction,
  cleanupOnUnmountAction,
};

export default compose(
  withCombinedRouteParams,
  withReducer('queryletReducer', queryletReducer, []),
  connect(mapStateToProps, mapDispatchToProps)
)(QueryContainer);
